import { useCallback, useEffect, useRef, useState } from "react";
import constants from "../constants/constants";
import Contact from "./contact.component";

export default function Testimonials() {
  const useScrollVisibility = () => {
    const slideRefs = useRef([]);

    const addToRefs = (el) => {
      if (el && !slideRefs.current.includes(el)) {
        slideRefs.current.push(el);
      }
    };

    const isScrolledIntoView = useCallback((elem) => {
      if (elem) {
        const rect = elem.getBoundingClientRect();
        const elemTop = rect.top;
        const elemBottom = rect.bottom;
        return elemTop >= 0 && elemBottom <= window.innerHeight;
      }
      return false;
    }, []);

    useEffect(() => {
      slideRefs.current.forEach((el) => {
        if (isScrolledIntoView(el)) {
          el?.classList.remove("slide-left");
        }
      });
    }, [isScrolledIntoView]);

    return { addToRefs };
  };

  const useWindowSize = () => {
    const [windowSize, setWindowSize] = useState(window.innerWidth);

    const onWindowResize = useCallback(() => {
      setWindowSize(window.innerWidth);
    }, []);

    useEffect(() => {
      window.addEventListener("resize", onWindowResize);
      return () => window.removeEventListener("resize", onWindowResize);
    }, [onWindowResize]);

    return windowSize;
  };

  const windowSize = useWindowSize();

  const { addToRefs } = useScrollVisibility();

  const [popup, setPopup] = useState(false);

  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    phoneNumber: "",
    rating: "",
    message: "",
    consent: false,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };


  async function submitForm(event) {
    event.preventDefault();

    const data = {
      ...formData,
      access_key: constants.EMAIL_ACCESS_KEY,
      subject: "New Review Form Message",
    };

    console.log("data", data);

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(data),
      });

      const response = await res.json();

      if (response?.success) {
        console.log("Success", response);
        setPopup({
          subject: "Thanks for your message!",
          message: "We will get back to you as soon as possible",
        });
        // Clear form after successful submission
        setFormData({
          firstname: "",
          lastname: "",
          email: "",
          phoneNumber: "",
          rating: "",
          message: "",
          consent: false,
        });
      } else {
        setPopup({
          subject: "We encountered an error in sending this message",
          message: "Please try again later",
        });
      }
    } catch (err) {
      console.log(err);
      setPopup({
        subject: "We encountered an error in sending this message",
        message: "Please try again later",
      });
    }
  }

  return (
    <>
      <div className="w-full max-w-[1400px] mx-auto px-7 mb-10 min-h-[70vh] pt-[190px]">
        <h1
          ref={addToRefs}
          className={`text-3xl md:text-4xl tracking-wider font-semibold uppercase text-black pb-0 lg:pb-8 libre duration-2000 slide-in text-center my-8 ${windowSize > 700 ? "slide-left" : ""
            }`}
        >
          Testimonials
        </h1>

        <div className="flex flex-col">
          {constants.TESTIMONIALS.map((testimonial, index) => (
            <div
              key={testimonial.name}
              className={`py-16 flex flex-col gap-10 ${index !== constants.TESTIMONIALS.length - 1
                ? "border-b border-gray-200"
                : ""
                }`}>
              {testimonial.quote.split("<|>").map((p, i) => (
                <p key={i} className="text-xl text-black tracking-wide font-light">{p}</p>
              ))}
              <p className="text-xl font-medium tracking-wide">–  {testimonial.name}</p>
            </div>
          ))}
        </div>

        <div className="my-12 flex flex-col gap-12">
          <h4 className="font-semibold text-black text-3xl md:text-4xl min-w-[20vw] text-center tracking-wide">
            Leave a Review
          </h4>
          <form onSubmit={submitForm} className="flex flex-col gap-6 px-5 lg:px-40">
            {/* Row 1 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col gap-2">
                <label htmlFor="firstname" className="text-xl font-light">First Name*</label>
                <input
                  className="review-form-input"
                  name="firstname"
                  placeholder="First Name"
                  value={formData.firstname}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="lastname" className="text-xl font-light">Last Name*</label>
                <input
                  className="review-form-input"
                  name="lastname"
                  placeholder="Last Name"
                  value={formData.lastname}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            {/* Row 2 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col gap-2">
                <label htmlFor="email" className="text-xl font-light">Email Address*</label>
                <input
                  type="email"
                  className="review-form-input"
                  name="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="phoneNumber" className="text-xl font-light">Phone Number*</label>
                <input
                  type="tel"
                  className="review-form-input"
                  name="phoneNumber"
                  placeholder="Phone Number"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            {/* Row 3 */}
            <div className="flex flex-col gap-2">
              <label htmlFor="rating" className="text-xl font-light">Rating*</label>
              <select
                className="review-form-input"
                name="rating"
                value={formData.rating}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Rating</option>
                <option value="1 Star">1 Star</option>
                <option value="2 Stars">2 Stars</option>
                <option value="3 Stars">3 Stars</option>
                <option value="4 Stars">4 Stars</option>
                <option value="5 Stars">5 Stars</option>
              </select>
            </div>

            {/* Row 4 */}
            <div className="flex flex-col gap-2">
              <label htmlFor="message" className="text-xl font-light">Message*</label>
              <textarea
                className="review-form-input resize-none h-40"
                name="message"
                placeholder="Share your experience..."
                value={formData.message}
                onChange={handleInputChange}
                required
              />
            </div>

            {/* Row 5 */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="consent"
                name="consent"
                checked={formData.consent}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    consent: e.target.checked,
                  })
                }
                required
                className="mt-1 h-4 w-4"
              />

              <label
                htmlFor="consent"
                className="text-sm md:text-lg font-light"
              >
                Yes, I agree to be contacted and receive helpful emails and
                understand I can unsubscribe at any time.
              </label>
            </div>

            {/* Row 6 */}
            <button
              type="submit"
              className="btn-secondary px-10 uppercase tracking-wider"
            >
              Submit
            </button>
          </form>

        </div>

        <Contact />
      </div>
      {popup && (
        <>
          <div className="backdrop" />
          <div className="popup">
            <h5 className="font-bold text-black text-lg">{popup.subject}</h5>
            <p className="font-semibold text-med-grey tracking-wide mb-3">
              {popup.message}
            </p>
            <button onClick={() => setPopup(false)} className="btn-secondary">
              Close
            </button>
          </div>
        </>
      )}
    </>
  )
}